import React from 'react';
import AnimalAvatar from '../../components/AnimalAvatar';
import './TypingIndicator.css';

/**
 * TypingIndicator — F4.1.
 *
 * Renders the design's `.typing` pill (glass surface, 3 bouncing dots).
 * The data layer lives in `typing.js`; the orchestrator subscribes and feeds
 * the filtered `users` prop (already self-excluded).
 *
 * Multi-user copy:
 *   - 1: "snowfox is typing"
 *   - 2: "naruto & rishav are typing"
 *   - 3+: "naruto, rishav & N others are typing"
 *
 * Props:
 *   - users: Array<{ id, handle, avatarSeed }>
 */
function formatLabel(users) {
  const names = users.map((u) => u.handle).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} is typing`;
  if (names.length === 2) return `${names[0]} & ${names[1]} are typing`;
  const others = names.length - 2;
  return `${names[0]}, ${names[1]} & ${others} ${
    others === 1 ? 'other' : 'others'
  } are typing`;
}

function TypingIndicator({ users = [] }) {
  if (!users || users.length === 0) return null;
  const label = formatLabel(users);
  // Use the first user's avatar as the pill's leading glyph — matches the
  // mini-bubble convention in the spec.
  const lead = users[0];

  return (
    <div
      className="glacier-typing"
      role="status"
      aria-live="polite"
      data-testid="typing-indicator"
    >
      <span className="glacier-typing__avatar" aria-hidden="true">
        <AnimalAvatar seed={lead.avatarSeed || lead.id || 'typing'} size={20} />
      </span>
      <span className="glacier-typing__text">{label}</span>
      <span className="glacier-typing__dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </div>
  );
}

export default TypingIndicator;
export const __test__ = { formatLabel };
